import { motion } from "framer-motion";
import { Mail, Linkedin, GraduationCap, BookOpen, Award, Github } from "lucide-react";
import { EMAIL, LINKEDIN_URL, QML_REPO_URL } from "@/constants/assets";

const CHIPS = [
  { icon: GraduationCap, t: "PhD · QUANTUM COMPUTING" },
  {
    icon: Award,
    t: "CERTIFIED · DATA SCIENCE & QUANTUM COMPUTING",
    sub: "UC DAVIS · IBM · KAIST · MITx PRO · BIMTECH",
  },
  { icon: BookOpen, t: "AUTHOR · 2 BOOKS · +10 RESEARCH PUBLICATIONS" },
  { icon: Github, t: "BUILDING REPLICANT · OPEN SOURCE" },
];

export default function Founder() {
  return (
    <section id="about" className="py-24 sm:py-32">
      <div className="max-w-7xl mx-auto px-5 sm:px-8">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.2 }}
          transition={{ duration: 0.7 }}
          className="mb-12"
        >
          <p className="text-gb-neon text-[11px] tracking-[0.3em] mb-3">09 · ABOUT</p>
          <h2 className="font-title text-6xl sm:text-7xl lg:text-8xl uppercase text-gb-pale leading-[0.85]">
            The person
            <br />
            <span className="text-outline">behind it.</span>
          </h2>
        </motion.div>

        <div className="grid lg:grid-cols-12 gap-8 items-start">
          {/* terminal card */}
          <motion.div
            initial={{ opacity: 0, x: -24 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true, amount: 0.2 }}
            transition={{ duration: 0.7 }}
            className="lg:col-span-5 border-2 border-gb-accent/40 bg-gb-darkest p-6 sm:p-8 shadow-[0_0_50px_rgba(139,172,15,0.10)]"
            data-testid="founder-card"
          >
            <p className="text-[10px] tracking-[0.25em] text-gb-accent mb-6">
              $ whoami
            </p>
            <h3 className="font-title text-4xl sm:text-5xl text-gb-pale leading-none">
              JAVIER
              <br />
              MANCILLA
              <br />
              <span className="text-gb-neon glow-text">MONTERO, PhD</span>
            </h3>
            <p className="mt-6 text-[12px] leading-relaxed text-gb-light/90">
              REPLICANT PROJECT
            </p>
            <div className="mt-8 flex flex-col gap-3">
              <a
                data-testid="founder-linkedin-link"
                href={LINKEDIN_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 text-xs tracking-[0.2em] text-gb-neon hover:text-gb-pale transition-colors w-max"
              >
                <Linkedin size={14} /> LINKEDIN/MANCILLAMONTERO
              </a>
              <a
                data-testid="founder-qml-repo-link"
                href={QML_REPO_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 text-xs tracking-[0.2em] text-gb-neon hover:text-gb-pale transition-colors w-max"
              >
                <Github size={14} /> GITHUB · QML-UNLOCKED (BOOK REPO)
              </a>
              <a
                data-testid="founder-email-link"
                href={`mailto:${EMAIL}`}
                className="inline-flex items-center gap-2 text-xs tracking-[0.2em] text-gb-neon hover:text-gb-pale transition-colors w-max"
              >
                <Mail size={14} /> {EMAIL}
              </a>
            </div>
          </motion.div>

          {/* bio */}
          <motion.div
            initial={{ opacity: 0, x: 24 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true, amount: 0.2 }}
            transition={{ duration: 0.7, delay: 0.1 }}
            className="lg:col-span-7"
          >
            <p className="text-base sm:text-lg leading-relaxed text-gb-mint">
              A researcher with a PhD in Quantum Computing and the author of
              two books on quantum computing.
            </p>
            <p className="mt-5 text-sm leading-relaxed text-gb-light/85">
              Javier wrote{" "}
              <span className="text-gb-pale">Financial Modeling Using Quantum Computing</span>{" "}
              (Packt, 2023) and <span className="text-gb-pale">QML Unlocked</span> (2025).
              replicant is his open-source project: simulating
              everyday life with deterministic kernels, LLM minds and
              quantum-probability beliefs, one small town at a time, built
              and shared in public.
            </p>

            <div className="mt-8 grid sm:grid-cols-2 gap-3">
              {CHIPS.map((c, i) => {
                const Icon = c.icon;
                return (
                  <motion.div
                    key={c.t}
                    initial={{ opacity: 0, y: 14 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, amount: 0.3 }}
                    transition={{ delay: i * 0.06, duration: 0.5 }}
                    className="flex items-center gap-3 border border-gb-line bg-gb-card px-4 py-3"
                    data-testid={`founder-credential-${i + 1}`}
                  >
                    <Icon size={14} className="text-gb-neon shrink-0" />
                    <div>
                      <span className="block text-[10px] sm:text-[11px] tracking-[0.12em] text-gb-light/90 leading-snug">
                        {c.t}
                      </span>
                      {c.sub && (
                        <span className="block mt-0.5 text-[9px] tracking-[0.1em] text-gb-accent leading-snug">
                          {c.sub}
                        </span>
                      )}
                    </div>
                  </motion.div>
                );
              })}
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
